/**
 * Product Hub — how much a store may sell.
 *
 * Three modes, three formulas, and one distinction the spec is emphatic about: an **allocation is
 * commercial, not physical** (ADR-013). Nothing here creates or moves stock. It reads the physical
 * total in the warehouses a store is eligible for — computed by `productHub.warehouse.ts` — and
 * answers what that store is permitted to sell out of it.
 *
 * The consequence worth stating: no mode ever invents stock. An allocation of 50 against 18 units
 * on the shelf sells 18, and the 50 remains as policy (§20). §25 puts it plainly: "אין לייצר מלאי
 * וירטואלי בגלל Allocation".
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through
 * `@malash-office/types/product-hub/inventory`.
 */
import { PhOption, phOptions } from './productHub.core';

/* ------------------------------------------------------------------ */
/* Modes                                                               */
/* ------------------------------------------------------------------ */

/** How a product's stock is divided between the stores that sell it (spec §23). */
export const INVENTORY_MODES = ['shared', 'allocated', 'hybrid'] as const;
export type InventoryMode = (typeof INVENTORY_MODES)[number];

export const INVENTORY_MODE_LABELS: Record<InventoryMode, string> = {
  shared: 'מלאי משותף',
  allocated: 'מלאי מוקצה',
  hybrid: 'משולב — מוגן + משותף'
};

export const INVENTORY_MODE_OPTIONS: PhOption<InventoryMode>[] = phOptions(INVENTORY_MODES, INVENTORY_MODE_LABELS);

/**
 * The same three modes, in the sentence that explains what a number on the screen means.
 *
 * `hybrid`'s is the one that has to be exact: a store's own protected quantity is **not** subtracted
 * from its own answer and is not a ceiling either — it sells its floor *and* whatever is left of the
 * shared pool. The spec's example is 100 in stock with A protected 20, B 10, C 0 giving 90 / 80 / 70,
 * which sums to more than 100 on purpose.
 *
 * Beside the labels rather than in a screen's `constants.ts`: two screens show it, and a hint that
 * disagrees with itself about `hybrid` is worse than no hint.
 */
export const INVENTORY_MODE_HINTS: Record<InventoryMode, string> = {
  shared:
    'כל החנויות מוכרות מתוך אותו מלאי מרכזי, בלי חלוקה. שתי חנויות יראו את אותה כמות זמינה — זו התצוגה הנכונה למלאי משותף.',
  allocated:
    'לכל חנות תקרה מסחרית. הזמינות היא המינימום בין ההקצאה למלאי בפועל: הקצאה של 50 מול 18 על המדף מוכרת 18, וה-50 נשאר כמדיניות.',
  hybrid:
    'מלאי משותף עם כמות שמורה לכל חנות. חנות מוכרת את המלאי הכולל בניכוי הכמויות המוגנות של החנויות האחרות — הכמות השמורה שלה עצמה אינה מנוכה ואינה תקרה.'
};

export const DEFAULT_INVENTORY_MODE: InventoryMode = 'shared';

/** Where a sellable number came from, so a screen can explain it rather than just print it. */
export const SELLABLE_SOURCES = [
  'shared_pool',
  'allocation_capped',
  'eligible_capped',
  'hybrid',
  'no_warehouse'
] as const;
export type SellableSource = (typeof SELLABLE_SOURCES)[number];

export const SELLABLE_SOURCE_LABELS: Record<SellableSource, string> = {
  shared_pool: 'מהמלאי המשותף',
  allocation_capped: 'מוגבל ע"י ההקצאה',
  eligible_capped: 'מוגבל ע"י המלאי במחסנים',
  hybrid: 'משותף בניכוי הכמויות המוגנות של חנויות אחרות',
  no_warehouse: 'אין מחסן זכאי'
};

/** What a store may sell, and why. */
export interface StoreSellable {
  sellable: number;
  mode: InventoryMode;
  source: SellableSource;
  sourceLabel: string;
  /** The physical total in the warehouses this store is eligible for. */
  eligibleAvailable: number;
  /** The `allocated`-mode policy figure, when one is set. Kept even when it is not the limit. */
  allocatedQuantity: number | null;
  /** This store's own protected quantity in `hybrid`. Never subtracted from its own answer. */
  protectedQuantity: number | null;
}

/** One store's allocation row, as the resolvers need it. */
export interface AllocationRow {
  storeId: number;
  allocatedQuantity: number | null;
  protectedQuantity: number | null;
}

const answer = (
  mode: InventoryMode,
  source: SellableSource,
  sellable: number,
  extra: Partial<StoreSellable> = {}
): StoreSellable => ({
  sellable: Math.max(0, sellable),
  mode,
  source,
  sourceLabel: SELLABLE_SOURCE_LABELS[source],
  eligibleAvailable: 0,
  allocatedQuantity: null,
  protectedQuantity: null,
  ...extra
});

/**
 * `shared` — every store draws from one pool (spec §24).
 *
 * The whole eligible total, with no per-store division at all. §24 is explicit that the system must
 * **not** show each store a separate figure as if it held stock physically; two stores both seeing
 * "40 available" out of one shelf of 40 is the correct display, and overselling is a problem for the
 * Orders domain to solve with real reservations, not for this function to pre-empt by inventing
 * fractions nobody configured.
 */
export const sharedSellable = (eligibleAvailable: number): StoreSellable =>
  answer('shared', 'shared_pool', eligibleAvailable, { eligibleAvailable });

/**
 * `allocated` — a commercial cap (spec §25, ADR-013).
 *
 * `min(allocation, eligibleAvailable)`. Both directions of that minimum matter:
 *
 *  - The allocation caps a store that would otherwise sell the whole shelf.
 *  - The shelf caps the allocation, because an allocation of 50 against 18 units cannot sell 20.
 *    The 50 stays as policy; the sellable number is 18 (§20's worked example).
 *
 * A null allocation means "no cap configured", which is `shared` behaviour rather than zero — a
 * store with no row must not silently stop selling.
 */
export const allocatedSellable = (eligibleAvailable: number, allocation: number | null): StoreSellable => {
  if (allocation === null) {
    return answer('allocated', 'shared_pool', eligibleAvailable, { eligibleAvailable, allocatedQuantity: null });
  }

  const capped = Math.min(allocation, eligibleAvailable);
  return answer(
    'allocated',
    // Which side won is the interesting part: "limited by your allocation" and "limited by the
    // warehouse" call for different actions from whoever is looking.
    allocation <= eligibleAvailable ? 'allocation_capped' : 'eligible_capped',
    capped,
    { eligibleAvailable, allocatedQuantity: allocation }
  );
};

/**
 * `hybrid` — a shared pool with a floor reserved for each store (spec §26).
 *
 * ```
 * StoreAvailable = max(0, TotalEligibleAvailable − Σ protected_quantity of all OTHER stores)
 * ```
 *
 * Two properties of that formula are easy to get wrong, and both are in the spec's worked example:
 *
 *  1. **A store's own protected quantity is never subtracted from its own answer.** It is
 *     protection *from* the others, not a ration.
 *  2. **It is not a ceiling either.** A store may sell its protected floor *and* whatever is left
 *     of the shared pool — which is why the answer is the total minus the others, rather than the
 *     store's own protection plus a share.
 *
 * The spec's example, which is a required test: 100 in stock, A protected 20, B protected 10, C
 * protected 0 → A = 90, B = 80, C = 70. Note the numbers sum to more than 100; that is correct and
 * is the point of a shared pool.
 */
export const hybridSellable = (
  eligibleAvailable: number,
  storeId: number,
  allocations: AllocationRow[]
): StoreSellable => {
  const othersProtected = allocations
    .filter(row => row.storeId !== storeId)
    .reduce((total, row) => total + (row.protectedQuantity ?? 0), 0);

  const own = allocations.find(row => row.storeId === storeId)?.protectedQuantity ?? null;

  return answer('hybrid', 'hybrid', eligibleAvailable - othersProtected, {
    eligibleAvailable,
    protectedQuantity: own
  });
};

/**
 * The one entry point: resolve by mode.
 *
 * A caller that switches on the mode itself is a caller that will one day handle two of the three,
 * so the switch lives here. `no_warehouse` is a distinct answer rather than a zero, because "we
 * have none" and "there is nowhere this store could be served from" need different fixes.
 */
export const resolveStoreSellable = (input: {
  mode: InventoryMode;
  storeId: number;
  eligibleAvailable: number;
  hasEligibleWarehouse: boolean;
  allocations: AllocationRow[];
}): StoreSellable => {
  if (!input.hasEligibleWarehouse) {
    return answer(input.mode, 'no_warehouse', 0);
  }

  switch (input.mode) {
    case 'shared':
      return sharedSellable(input.eligibleAvailable);

    case 'allocated': {
      const row = input.allocations.find(candidate => candidate.storeId === input.storeId);
      return allocatedSellable(input.eligibleAvailable, row?.allocatedQuantity ?? null);
    }

    case 'hybrid':
      return hybridSellable(input.eligibleAvailable, input.storeId, input.allocations);
  }
};

/* ------------------------------------------------------------------ */
/* Anomalies                                                           */
/* ------------------------------------------------------------------ */

/**
 * The states doc 2 §24 says to **flag, not forbid**.
 *
 * The spec's own words: some of these are a warning and not a schema error — "an allocation of 50
 * against actual stock of 20 is a possible business state that needs an indication". Encoding them
 * as codes rather than sentences means the dashboard and a future alerting job agree on what they
 * are called.
 */
export const INVENTORY_ANOMALIES = [
  'negative_stock',
  'no_eligible_warehouse',
  'store_empty_while_stock_elsewhere',
  'stuck_transfer',
  'allocation_above_available',
  'protected_above_available',
  'archived_warehouse_holding_stock',
  /** §24's "active product with no fulfilment path" — landed with the fulfillment module in PR 13. */
  'no_fulfillment_path'
] as const;
export type InventoryAnomaly = (typeof INVENTORY_ANOMALIES)[number];

export const INVENTORY_ANOMALY_LABELS: Record<InventoryAnomaly, string> = {
  negative_stock: 'מלאי שלילי',
  no_eligible_warehouse: 'מוצר פעיל ללא מחסן זכאי',
  store_empty_while_stock_elsewhere: 'חנות ללא מלאי למרות מלאי במחסן אחר',
  stuck_transfer: 'העברה תקועה',
  allocation_above_available: 'הקצאה גבוהה מהזמינות בפועל',
  protected_above_available: 'כמויות מוגנות גבוהות מהמלאי',
  archived_warehouse_holding_stock: 'מחסן בארכיון שמחזיק מלאי',
  no_fulfillment_path: 'מוצר פעיל ללא דרך אספקה'
};

export const INVENTORY_ANOMALY_OPTIONS = phOptions(INVENTORY_ANOMALIES, INVENTORY_ANOMALY_LABELS);

/** How loudly to draw it. Only genuine data corruption is an error; the rest need a human's eye. */
export const ANOMALY_SEVERITY: Record<InventoryAnomaly, 'error' | 'warning'> = {
  negative_stock: 'error',
  archived_warehouse_holding_stock: 'error',
  no_eligible_warehouse: 'warning',
  store_empty_while_stock_elsewhere: 'warning',
  stuck_transfer: 'warning',
  allocation_above_available: 'warning',
  protected_above_available: 'warning',
  no_fulfillment_path: 'warning'
};

/** A transfer sitting in transit longer than this is flagged as stuck. */
export const STUCK_TRANSFER_DAYS = 7;

/* ------------------------------------------------------------------ */
/* Transfers                                                           */
/* ------------------------------------------------------------------ */

/** A transfer's lifecycle (spec §30). */
export const TRANSFER_STATUSES = ['draft', 'in_transit', 'received', 'cancelled'] as const;
export type TransferStatus = (typeof TRANSFER_STATUSES)[number];

export const TRANSFER_STATUS_LABELS: Record<TransferStatus, string> = {
  draft: 'טיוטה',
  in_transit: 'במעבר',
  received: 'נקלטה',
  cancelled: 'בוטלה'
};

export const TRANSFER_STATUS_OPTIONS = phOptions(TRANSFER_STATUSES, TRANSFER_STATUS_LABELS);

/**
 * Which transitions exist.
 *
 * `received` and `cancelled` are terminal, and a received transfer explicitly cannot be cancelled:
 * the stock has already landed, and "undoing" it would need an adjustment with a reason, not a
 * status flip that silently rewrites where the units are.
 */
export const TRANSFER_TRANSITIONS: Record<TransferStatus, TransferStatus[]> = {
  draft: ['in_transit', 'cancelled'],
  in_transit: ['received', 'cancelled'],
  received: [],
  cancelled: []
};

export const canTransition = (from: TransferStatus, to: TransferStatus): boolean =>
  TRANSFER_TRANSITIONS[from].includes(to);
